import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { contactInfo, faqData } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';
import { sanitizeInput, isValidEmail, isValidPhone, isValidRequired } from '../utils/sanitize';

const ContactPage = () => {
  // EmailJS Configuration from environment variables
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    productInterest: '',
    orderQuantity: '',
    message: ''
  });

  const [quickQuoteData, setQuickQuoteData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQuickSubmitting, setIsQuickSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuickQuoteChange = (e) => {
    const { name, value } = e.target;
    setQuickQuoteData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate inputs
      if (!isValidRequired(formData.name, 2)) {
        alert('Please enter a valid name (minimum 2 characters)');
        setIsSubmitting(false);
        return;
      }

      if (!isValidEmail(formData.email)) {
        alert('Please enter a valid email address');
        setIsSubmitting(false);
        return;
      }

      if (!isValidRequired(formData.company, 2)) {
        alert('Please enter a valid company name');
        setIsSubmitting(false);
        return;
      }

      if (formData.phone && !isValidPhone(formData.phone)) {
        alert('Please enter a valid phone number');
        setIsSubmitting(false);
        return;
      }

      // Sanitize all inputs before sending
      const sanitizedData = {
        name: sanitizeInput(formData.name, 100),
        email: sanitizeInput(formData.email, 254),
        company: sanitizeInput(formData.company, 200),
        phone: formData.phone ? sanitizeInput(formData.phone, 20) : 'Not provided',
        productInterest: sanitizeInput(formData.productInterest, 100),
        orderQuantity: sanitizeInput(formData.orderQuantity, 100),
        message: sanitizeInput(formData.message, 2000)
      };

      // Send email using EmailJS with sanitized data
      const templateParams = {
        to_name: 'KATO MITO Team',
        from_name: sanitizedData.name,
        from_email: sanitizedData.email,
        phone: sanitizedData.phone,
        company: sanitizedData.company,
        subject: `Quote Request - ${sanitizedData.productInterest || 'General Inquiry'}`,
        message: `Company: ${sanitizedData.company}\nProduct Interest: ${sanitizedData.productInterest || 'Not specified'}\nOrder Quantity: ${sanitizedData.orderQuantity || 'Not specified'}\n\nMessage:\n${sanitizedData.message || 'No additional message'}`,
        reply_to: sanitizedData.email
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('Email sent successfully:', response);
      alert('Thank you for your inquiry! We will contact you within 24 hours.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        productInterest: '',
        orderQuantity: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Sorry, there was an error sending your message. Please try again or contact us directly at info@katomito.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsQuickSubmitting(true);

    try {
      // Validate inputs
      if (!isValidRequired(quickQuoteData.name, 2)) {
        alert('Please enter a valid name (minimum 2 characters)');
        setIsQuickSubmitting(false);
        return;
      }

      if (!isValidEmail(quickQuoteData.email)) {
        alert('Please enter a valid email address');
        setIsQuickSubmitting(false);
        return;
      }

      if (!isValidRequired(quickQuoteData.company, 2)) {
        alert('Please enter a valid company name');
        setIsQuickSubmitting(false);
        return;
      }

      if (!quickQuoteData.subject) {
        alert('Please select what we can help you with');
        setIsQuickSubmitting(false);
        return;
      }

      // Sanitize all inputs before sending
      const sanitizedData = {
        name: sanitizeInput(quickQuoteData.name, 100),
        email: sanitizeInput(quickQuoteData.email, 254),
        company: sanitizeInput(quickQuoteData.company, 200),
        subject: sanitizeInput(quickQuoteData.subject, 100),
        message: sanitizeInput(quickQuoteData.message, 2000)
      };

      // Send email using EmailJS with sanitized data
      const templateParams = {
        to_name: 'KATO MITO Team',
        from_name: sanitizedData.name,
        from_email: sanitizedData.email,
        phone: 'Not provided',
        company: sanitizedData.company,
        subject: sanitizedData.subject || 'Quick Quote Request',
        message: `Company: ${sanitizedData.company}\nInquiry Type: ${sanitizedData.subject}\n\nMessage:\n${sanitizedData.message || 'No additional message'}`,
        reply_to: sanitizedData.email
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('Email sent successfully:', response);
      alert('Thank you for your quote request! We will contact you within 24 hours.');
      
      // Reset form
      setQuickQuoteData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Sorry, there was an error sending your message. Please try again or contact us directly at info@katomito.com');
    } finally {
      setIsQuickSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'EMAIL US',
      description: 'Send us a message anytime',
      value: contactInfo.email,
      action: `mailto:${contactInfo.email}`,
      color: 'text-kato-cyan',
      bgColor: 'bg-kato-cyan'
    },
    {
      icon: Phone,
      title: 'CALL US',
      description: 'Speak with our sales team',
      value: contactInfo.phone,
      action: `tel:${contactInfo.phone}`,
      color: 'text-kato-yellow',
      bgColor: 'bg-kato-yellow'
    },
    {
      icon: MapPin,
      title: 'VISIT US',
      description: 'Our business location',
      value: contactInfo.address,
      action: '#',
      color: 'text-kato-navy',
      bgColor: 'bg-kato-navy'
    },
    {
      icon: Clock,
      title: 'BUSINESS HOURS',
      description: 'When we\'re available',
      value: contactInfo.businessHours,
      action: '#',
      color: 'text-kato-cyan',
      bgColor: 'bg-kato-cyan'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Us | Global Swedish Candy Wholesale - International Quote Requests"
        description="Contact KATO MITO for international bulk wholesale Swedish candy quotes. Premium foamy candy, skull candy & custom confections shipped worldwide. Global B2B candy supplier serving North America, Europe, Asia, Australia, Middle East, South America & Africa. Get your wholesale quote today!"
        keywords="Swedish candy wholesale quote, international candy supplier, global bulk candy, foamy candy worldwide, skull candy international, Swedish candy distributor global, wholesale candy quote worldwide"
        canonical={`${process.env.REACT_APP_BASE_URL || "https://katomito.com"}/contact`}
      />
      
      {/* Get Quote Section - Direct Access */}
      <section id="quote" className="py-20 bg-gradient-to-r from-kato-yellow to-kato-cyan">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fadeInUp">
            <h2 className="text-5xl md:text-6xl font-black text-kato-navy mb-8">
              GET YOUR QUOTE! 🍭
            </h2>
            <p className="text-2xl text-kato-navy mb-12 font-medium">
              Ready to sweeten your business worldwide? Let's create happy memories together across continents!
            </p>
            
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
              <form onSubmit={handleQuickQuoteSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    name="name"
                    value={quickQuoteData.name}
                    onChange={handleQuickQuoteChange}
                    placeholder="Your Name"
                    className="w-full p-4 rounded-xl border-2 border-kato-navy/20 focus:border-kato-cyan text-lg"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    value={quickQuoteData.email}
                    onChange={handleQuickQuoteChange}
                    placeholder="Email Address"
                    className="w-full p-4 rounded-xl border-2 border-kato-navy/20 focus:border-kato-cyan text-lg"
                    required
                  />
                </div>
                
                <input
                  type="text"
                  name="company"
                  value={quickQuoteData.company}
                  onChange={handleQuickQuoteChange}
                  placeholder="Company Name"
                  className="w-full p-4 rounded-xl border-2 border-kato-navy/20 focus:border-kato-cyan text-lg"
                  required
                />
                
                <select 
                  name="subject"
                  value={quickQuoteData.subject}
                  onChange={handleQuickQuoteChange}
                  className="w-full p-4 rounded-xl border-2 border-kato-navy/20 focus:border-kato-cyan text-lg"
                  required
                >
                  <option value="">What can we help you with?</option>
                  <option value="4OZ Variety Packs Quote">4OZ Variety Packs Quote</option>
                  <option value="5LB Bulk Packs Quote">5LB Bulk Packs Quote</option>
                  <option value="Custom Candy Creation">Custom Candy Creation</option>
                  <option value="Private Label Options">Private Label Options</option>
                  <option value="General Wholesale Inquiry">General Wholesale Inquiry</option>
                </select>
                
                <textarea
                  name="message"
                  value={quickQuoteData.message}
                  onChange={handleQuickQuoteChange}
                  rows="4"
                  placeholder="Tell us about your sweet dreams! What quantity do you need? Any special requirements?"
                  className="w-full p-4 rounded-xl border-2 border-kato-navy/20 focus:border-kato-cyan text-lg"
                ></textarea>
                
                <button
                  type="submit"
                  disabled={isQuickSubmitting}
                  className="bg-kato-navy text-white px-12 py-4 rounded-full text-xl font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isQuickSubmitting ? 'Sending...' : 'Send My Sweet Request! 🚀'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section - Full Impact */}
      <section className="relative min-h-[70vh] bg-gradient-to-br from-kato-navy via-purple-900 to-kato-cyan text-white flex items-center overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-24 h-24 bg-kato-yellow rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute top-60 right-20 w-32 h-32 bg-kato-cyan rounded-full opacity-10 animate-bounce"></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-kato-yellow rounded-full opacity-30 animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center animate-fadeInUp">
            <div className="mb-8">
              <span className="inline-block bg-kato-yellow text-kato-navy px-8 py-3 rounded-full text-xl font-black mb-6 animate-bounce">
                🤝 LET'S PARTNER UP
              </span>
            </div>
            
            <h1 className="text-6xl md:text-8xl font-black mb-8 leading-none">
              GET IN
              <span className="block text-kato-yellow animate-pulse">TOUCH</span>
            </h1>
            
            <p className="text-2xl md:text-3xl opacity-90 max-w-4xl mx-auto font-medium leading-relaxed">
              Ready to <span className="text-kato-yellow font-black">join our global partner network?</span> We're here to help you find the 
              <span className="text-kato-cyan font-black"> perfect Swedish-style candy solution</span> for your market, wherever you are in the world.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods - Bold Cards */}
      <section className="py-16 -mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 group border-4 border-transparent hover:border-kato-cyan"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className={`w-16 h-16 ${method.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-black text-kato-navy mb-3 text-center group-hover:text-kato-cyan transition-colors">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-center">
                    {method.description}
                  </p>
                  <a 
                    href={method.action}
                    className={`block text-center font-bold ${method.color} hover:underline text-lg`}
                  >
                    {method.value}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form and FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="animate-slideInLeft">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-transparent hover:border-kato-cyan transition-all duration-300">
                <div className="mb-8">
                  <h2 className="text-4xl font-black text-kato-navy mb-4 flex items-center">
                    <MessageSquare className="w-10 h-10 mr-4 text-kato-cyan" />
                    REQUEST A QUOTE
                  </h2>
                  <p className="text-xl text-gray-600">
                    Fill out the form below and we'll get back to you within 24 hours with international pricing, shipping details, and availability for your region.
                  </p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-black text-gray-700 mb-3">
                        FULL NAME *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="focus-ring text-lg p-4 rounded-xl border-2 border-gray-300 focus:border-kato-cyan"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-lg font-black text-gray-700 mb-3">
                        EMAIL ADDRESS *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="focus-ring text-lg p-4 rounded-xl border-2 border-gray-300 focus:border-kato-cyan"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-black text-gray-700 mb-3">
                        COMPANY NAME *
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        className="focus-ring text-lg p-4 rounded-xl border-2 border-gray-300 focus:border-kato-cyan"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label className="block text-lg font-black text-gray-700 mb-3">
                        PHONE NUMBER
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="focus-ring text-lg p-4 rounded-xl border-2 border-gray-300 focus:border-kato-cyan"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-black text-gray-700 mb-3">
                        PRODUCT INTEREST
                      </label>
                      <select
                        name="productInterest"
                        value={formData.productInterest}
                        onChange={handleInputChange}
                        className="w-full p-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-kato-cyan text-lg"
                      >
                        <option value="">Select a product</option>
                        <option value="4oz-packs">4OZ Variety Packs</option>
                        <option value="5lb-bulk">5LB Bulk Packs</option>
                        <option value="custom">Custom & Private Label</option>
                        <option value="mixed">Mixed Order</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-lg font-black text-gray-700 mb-3">
                        ORDER QUANTITY
                      </label>
                      <Input
                        type="text"
                        name="orderQuantity"
                        value={formData.orderQuantity}
                        onChange={handleInputChange}
                        className="focus-ring text-lg p-4 rounded-xl border-2 border-gray-300 focus:border-kato-cyan"
                        placeholder="e.g., 100 units"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-black text-gray-700 mb-3">
                      MESSAGE
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="focus-ring text-lg p-4 rounded-xl border-2 border-gray-300 focus:border-kato-cyan"
                      placeholder="Tell us about your business needs, timeline, or any specific requirements..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-kato-navy text-white py-6 text-xl font-black hover:bg-opacity-90 rounded-2xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-6 h-6 mr-3" />
                    {isSubmitting ? 'SENDING...' : 'SEND INQUIRY'}
                  </Button>
                </form>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="animate-slideInRight">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-transparent hover:border-kato-yellow transition-all duration-300">
                <div className="mb-8">
                  <h2 className="text-4xl font-black text-kato-navy mb-4">
                    FREQUENTLY ASKED QUESTIONS
                  </h2>
                  <p className="text-xl text-gray-600">
                    Quick answers to common questions about our products and services.
                  </p>
                </div>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {faqData.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`} className="border-2 border-gray-200 rounded-xl px-6 hover:border-kato-cyan transition-colors">
                      <AccordionTrigger className="text-left text-kato-navy hover:text-kato-cyan font-bold text-lg py-6">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 leading-relaxed text-lg pb-6">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                <div className="mt-8 p-8 bg-gradient-to-r from-kato-yellow to-kato-cyan rounded-2xl">
                  <h4 className="font-black text-kato-navy mb-4 text-2xl">
                    STILL HAVE QUESTIONS?
                  </h4>
                  <p className="text-kato-navy mb-6 text-lg">
                    Our wholesale team is here to help you find the perfect solution for your business.
                  </p>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="inline-flex items-center text-kato-navy font-black hover:underline text-xl"
                  >
                    <Mail className="w-6 h-6 mr-3" />
                    EMAIL OUR TEAM DIRECTLY
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;